import React, { useRef, useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/scrollAnimations.css';
import './PlatformShowcase.css';

const VIDEO_SRC = '/img/bonusclub_video.mp4';
const XRAAY_BG = `${process.env.PUBLIC_URL || ''}/img/xraay.jpg`;

const PlatformShowcase = () => {
  const { language } = useLanguage();
  const t = translations[language].platformShowcase;
  const [sectionRef, sectionVisible] = useScrollAnimation({ threshold: 0.1 });
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });
  const videoRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => setIsLoaded(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setIsLoaded(true);

    return () => video.removeEventListener('canplay', onCanPlay);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isLoaded) return;

    if (sectionVisible && !isActive) {
      video.play().catch(() => {});
    }
  }, [sectionVisible, isLoaded, isActive]);

  const handlePlayClick = () => {
    const video = videoRef.current;
    if (!video) return;

    setIsActive(true);
    setIsPaused(false);
    video.currentTime = 0;
    video.muted = false;
    video.loop = false;
    video.play().catch(() => {});
  };

  const handlePauseToggle = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPaused) {
      video.play().catch(() => {});
      setIsPaused(false);
    } else {
      video.pause();
      setIsPaused(true);
    }
  };

  const handleVideoEnd = () => {
    const video = videoRef.current;
    if (!video) return;

    setIsActive(false);
    setIsPaused(false);
    video.muted = true;
    video.loop = true;
    video.play().catch(() => {});
  };

  return (
    <section
      className="platform-showcase"
      style={{ '--platform-showcase-bg-image': `url("${XRAAY_BG}")` }}
    >
      <div className="platform-showcase-inner">
        <span
          ref={titleRef}
          className={`section-label scroll-fade-in ${titleVisible ? 'is-visible' : ''}`}
        >
          {t.label}
        </span>
        <h2 className={`platform-showcase-title scroll-fade-in scroll-animation-delay-1 ${titleVisible ? 'is-visible' : ''}`}>
          {t.titleHighlightInline ? (
            <>
              {t.titleLine1}
              <span className="platform-showcase-title-line2">
                {t.titleLine2Pre}
                <span className="platform-showcase-title-silver">{t.titleLine2Highlight}</span>
                {t.titleLine2Post}
              </span>
            </>
          ) : (
            <>
              {t.titleLine1}
              {(t.titleLine2Pre || t.titleLine2Highlight || t.titleLine2Post) && (
                <>
                  <br />
                  <span className="platform-showcase-title-line2">
                    {t.titleLine2Pre}
                    <span className="platform-showcase-title-silver">{t.titleLine2Highlight}</span>
                    {t.titleLine2Post}
                  </span>
                </>
              )}
            </>
          )}
        </h2>
        <p className={`platform-showcase-subtitle scroll-fade-in scroll-animation-delay-2 ${titleVisible ? 'is-visible' : ''}`}>
          {t.subtitle}
        </p>

        <div
          ref={sectionRef}
          className={`platform-showcase-frame scroll-fade-in-scale scroll-animation-delay-2 ${sectionVisible ? 'is-visible' : ''}`}
        >
          <div className="platform-showcase-glow" />
          <div className="platform-showcase-border">
            <div className="platform-showcase-screen">
              <video
                ref={videoRef}
                src={VIDEO_SRC}
                muted
                loop
                playsInline
                preload="auto"
                onEnded={handleVideoEnd}
                className={`platform-showcase-video ${isActive ? 'platform-showcase-video-active' : ''} ${isLoaded ? 'platform-showcase-video-loaded' : ''}`}
              />

              {/* Play overlay (before playing) */}
              <div
                className={`platform-showcase-overlay ${isActive ? 'platform-showcase-overlay-hidden' : ''} ${isLoaded ? 'platform-showcase-overlay-ambient' : ''}`}
                onClick={handlePlayClick}
              >
                <div className="platform-showcase-play-btn">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                </div>
                <span className="platform-showcase-play-text">{t.playText}</span>
              </div>

              {/* Pause button (while playing) */}
              {isActive && (
                <div
                  className={`platform-showcase-pause-zone ${isPaused ? 'platform-showcase-pause-zone-visible' : ''}`}
                  onClick={handlePauseToggle}
                >
                  <div className={`platform-showcase-pause-btn ${isPaused ? 'platform-showcase-pause-btn-visible' : ''}`}>
                    {isPaused ? (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="4" width="4" height="16" rx="1" />
                        <rect x="14" y="4" width="4" height="16" rx="1" />
                      </svg>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <p className="platform-showcase-credit">Julian Propst - Co-Founder</p>
      </div>
    </section>
  );
};

export default PlatformShowcase;
